import React from 'react';

import Input, { InputProps } from '@opendoor/bricks/core/Input';
import { isNil } from 'lodash';

import { IAccountCombinedFormProps } from './AccountCombinedForm';
import AccountFormControls from './AccountFormControls';
import { Form } from './shared';

const INVALID_EMAIL_ERROR = 'Please enter a valid email.';

export interface IAccountEmailFormProps {
  /**
   * Name of the account form in analytics events, should be unique within the app
   * Convention: {appName}-{accountFormName}
   */
  analyticsName: string;
  email?: string;
  loading?: boolean;
  onSubmit: (email: string, fullName?: string) => void;
  customEmailValidityCheck?: (email: string) => Promise<{ suggestion: string; valid: boolean }>;
  customEmailValidityCheckDelay?: number;
  openForgotPasswordInNewTab?: boolean;
  showForgotPassword?: boolean;
  EmailInput?: IAccountCombinedFormProps['EmailInput'];
}

export interface IAccountEmailFormState {
  email: string;
  hasError: boolean;
}

export default class AccountEmailForm extends React.Component<
  IAccountEmailFormProps,
  IAccountEmailFormState
> {
  public static defaultProps = {
    email: '',
    showForgotPassword: true,
    EmailInput: Input,
  };

  public state: IAccountEmailFormState = {
    email: this.props.email,
    hasError: false,
  };

  public componentDidMount() {
    this.maybePrefillEmail();
  }

  public onChange: InputProps['onChange'] = (_event, value, validFormat) => {
    const email = isNil(value) ? '' : value.trim();
    this.setState({ email, hasError: isNil(value) || !validFormat });
  };

  public handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { onSubmit } = this.props;
    const { email, hasError } = this.state;

    if (!hasError) {
      onSubmit(email);
    }
  };

  public render() {
    const {
      analyticsName,
      loading,
      openForgotPasswordInNewTab,
      customEmailValidityCheck,
      customEmailValidityCheckDelay,
      showForgotPassword,
      EmailInput,
    } = this.props;

    // Disable the form if it has an error or if email is not set.
    const isDisabled = this.state.hasError || !this.state.email;
    return (
      <Form onSubmitCapture={this.handleSubmit}>
        <EmailInput
          analyticsName={`${analyticsName}-email`}
          required
          unboundedWidth
          type="email"
          id="email-login-input"
          name="email"
          minLength={1}
          value={this.props.email}
          errorMessage={INVALID_EMAIL_ERROR}
          onChange={this.onChange}
          placeholder={'you@example.com'}
          label="Email"
          width="100%"
          fontSize="s1"
          delay={customEmailValidityCheckDelay ?? 0}
          customEmailValidityCheck={customEmailValidityCheck}
          autoComplete="email"
        />
        <AccountFormControls
          actionText="Continue with email"
          analyticsName={`${analyticsName}-email`}
          loading={loading}
          disabled={isDisabled}
          openForgotPasswordInNewTab={openForgotPasswordInNewTab}
          submitA11yProps={{ 'aria-label': '' }}
          showForgotPassword={showForgotPassword}
        />
      </Form>
    );
  }

  private maybePrefillEmail = () => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.searchParams);

    // Update state on mount only if there is a query param override
    if (params.get('email')) {
      this.setState({ email: params.get('email') || '' });
    }
  };
}
